// Banner over a "Try it" world preview: what you are walking through, and
// how to buy it without leaving. Checkout opens in the same in-site sheet as
// the storefront; on success the buyer lands on the storefront, which waits
// for delivery.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, ShoppingBag, X } from 'lucide-react';
import CheckoutModal from '../checkout/CheckoutModal';
import { supabase } from '../../config/supabase';
import { MARKETPLACE_PURCHASES_ENABLED } from '../../config/marketplace';
import { formatMoney, startListingCheckout } from './buyListing';
import './PreviewBanner.css';

export interface PreviewListing {
  id: string;
  title: string;
  price_cents: number;
  currency: string;
  sale_type: 'copy' | 'original';
  creator_id: string;
  thumbnail_url: string | null;
  purchasable: boolean;
}

const PreviewBanner: React.FC<{ listing: PreviewListing }> = ({ listing }) => {
  const navigate = useNavigate();
  const [paying, setPaying] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [me, setMe] = useState<string | null | undefined>(undefined);
  const [owned, setOwned] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setMe(session?.user?.id ?? null);
      if (session) {
        const { data } = await supabase.from('marketplace_entitlements')
          .select('listing_id').eq('listing_id', listing.id).is('revoked_at', null).limit(1);
        setOwned(!!data?.length);
      }
    })();
  }, [listing.id]);

  const price = formatMoney(listing.price_cents, listing.currency);
  const mine = me === listing.creator_id;

  const buy = () => {
    if (!me) {
      navigate(`/login?next=${encodeURIComponent(`/marketplace/preview/${listing.id}`)}`);
      return;
    }
    setPaying(true);
  };

  if (collapsed) {
    return (
      <button className="gx-preview-pill" onClick={() => setCollapsed(false)}>
        <Eye size={14} /> Preview · {price}
      </button>
    );
  }

  return (
    <>
      <div className="gx-preview-banner" role="status">
        <Eye size={16} className="gx-preview-eye" />
        <div className="gx-preview-text">
          <strong>Previewing {listing.title}</strong>
          <span>
            {listing.sale_type === 'original' ? 'The original · one of one · ' : ''}
            Look around — nothing here is saved, and nobody else is in this preview.
          </span>
        </div>
        {owned ? (
          <span className="gx-preview-owned">You own this</span>
        ) : mine ? (
          <span className="gx-preview-owned">Your listing</span>
        ) : MARKETPLACE_PURCHASES_ENABLED && listing.purchasable ? (
          <button className="gx-preview-buy" onClick={buy}>
            <ShoppingBag size={14} /> Buy for {price}
          </button>
        ) : (
          <span className="gx-preview-owned" title="The creator hasn't finished payout setup yet.">Coming soon · {price}</span>
        )}
        <button className="gx-preview-x" onClick={() => setCollapsed(true)} aria-label="Hide banner"><X size={15} /></button>
      </div>

      {paying && (
        <CheckoutModal
          custom={{
            title: `${listing.title} · ${price}`,
            blurb: listing.sale_type === 'original'
              ? 'The original: it moves to you and leaves the seller.'
              : 'Your own copy of this world, ready to edit, the moment payment clears.',
            start: () => startListingCheckout(listing.id),
            onComplete: () => navigate(`/marketplace?purchased=${listing.id}`),
          }}
          onClose={() => setPaying(false)}
        />
      )}
    </>
  );
};

export default PreviewBanner;
